import { api } from "@/api/base-api";

export type HarnessPaymentCountry = {
  id: number;
  name: string | null;
  abbreviation: string | null;
};

export type HarnessPaymentState = {
  id: number;
  countryId: number;
  name: string | null;
  abbreviation: string | null;
};

export type HarnessPaymentTokenizationDetail = {
  tokenSrc: string | null;
  scriptProperties: Record<string, string | null> | null;
  creditCardSurchargePercent: number;
  allowUnknownCardTypeProcessing: boolean;
};

export type HarnessPaymentMethodBillingAddress = {
  firstName?: string | null
  lastName?: string | null
  company?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  stateId?: number | null
  postalCode?: string | null
  countryId?: number | null
  phone?: string | null
  fax?: string | null
  email?: string | null
}

export type HarnessPaymentMethod = {
  id: number;
  paymentMethodType: string | null;
  cardType: string | null;
  cardCategory: string | null;
  maskedAccountNumber: string | null;
  accountHolderName: string | null;
  paymentMethodNickname: string | null;
  expirationDate: string | null;
  accountType: string | null;
  accountHolderType: string | null;
  billingZipCode: string | null;
  isDefaultPaymentMethod: boolean;
  disabled: boolean;
};

export type AddHarnessCustomerPaymentMethodBody = {
  paymentToken: string
  paymentMethodType?: string | null
  paymentMethodNickname: string | null
  cardType: string | null
  cardCategory: string | null
  achAccountType: string | null
  achAccountHolderType: string | null
  billingAddress: HarnessPaymentMethodBillingAddress
  isDefault: boolean
}


export type AddHarnessCustomerPaymentMethodArgs = {
  customerId: number;
  body: AddHarnessCustomerPaymentMethodBody;
};

export type GetHarnessCustomerPaymentMethodsQueryArgs = {
  customerId: number;
};

export type GetHarnessPaymentStatesQueryArgs = {
  countryId?: number;
};

export type UpdateHarnessCustomerPaymentMethodBody = {
  disabled?: boolean | null;
  isDefaultPaymentMethod?: boolean | null;
};

export type UpdateHarnessCustomerPaymentMethodArgs = {
  customerId: number;
  paymentMethodId: number;
  body: UpdateHarnessCustomerPaymentMethodBody;
};

export type DeleteHarnessCustomerPaymentMethodArgs = {
  customerId: number;
  paymentMethodId: number;
};

const paymentMethodsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getHarnessPaymentTokenizationDetail: builder.query<
      HarnessPaymentTokenizationDetail,
      void
    >({
      query: () => ({
        url: "/api/providers/Harness/Payment/TokenizationDetail",
      }),
    }),
    getHarnessPaymentCountries: builder.query<HarnessPaymentCountry[], void>({
      query: () => ({
        url: "/api/providers/Harness/Payment/Countries",
      }),
    }),
    getHarnessPaymentStates: builder.query<
      HarnessPaymentState[],
      GetHarnessPaymentStatesQueryArgs
    >({
      query: ({ countryId }) => ({
        url: "/api/providers/Harness/Payment/States",
        params: countryId ? { countryId } : undefined,
      }),
    }),
    getHarnessCustomerPaymentMethods: builder.query<
      HarnessPaymentMethod[],
      GetHarnessCustomerPaymentMethodsQueryArgs
    >({
      query: ({ customerId }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/PaymentMethod`,
      }),
    }),
    addHarnessCustomerPaymentMethod: builder.mutation<
      void,
      AddHarnessCustomerPaymentMethodArgs
    >({
      query: ({ customerId, body }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/PaymentMethod`,
        method: "POST",
        body,
      }),
    }),
    updateHarnessCustomerPaymentMethod: builder.mutation<
      HarnessPaymentMethod,
      UpdateHarnessCustomerPaymentMethodArgs
    >({
      query: ({ customerId, paymentMethodId, body }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/PaymentMethod/${paymentMethodId}`,
        method: "PATCH",
        body,
      }),
    }),
    deleteHarnessCustomerPaymentMethod: builder.mutation<
      void,
      DeleteHarnessCustomerPaymentMethodArgs
    >({
      query: ({ customerId, paymentMethodId }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/PaymentMethod/${paymentMethodId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetHarnessPaymentTokenizationDetailQuery,
  useGetHarnessPaymentCountriesQuery,
  useGetHarnessPaymentStatesQuery,
  useGetHarnessCustomerPaymentMethodsQuery,
  useAddHarnessCustomerPaymentMethodMutation,
  useUpdateHarnessCustomerPaymentMethodMutation,
  useDeleteHarnessCustomerPaymentMethodMutation,
} = paymentMethodsApi;
